import { Request } from 'express';

import {
  allClientDomainHostnames,
  allPrimaryDomainHostnames,
  assemblyPrimaryDomainHostname,
  primaryDomainHostname,
} from 'src/constants/hostnameConsts';
import { PortalConfig } from 'src/context';
import history from 'src/history';
import { AddressCustomFieldValues } from 'src/store/clients/types';
import { isNonDeployedHost } from 'src/utils/posthogHostGate';

interface UrlOptions {
  removeTrailingSlash?: boolean;
}

export default class UrlUtils {
  /**
   * Returns base route from given short link url
   * For example if given url is in format '{host}/sl/form/{ref_id}' then return 'forms'
   * */
  static getBaseRouteFromShortLinkURL(baseUrl: string) {
    // contains mapping between short-link code and the base path to route to
    const shortLinks: Map<string, string> = new Map([
      ['form', 'forms'],
      ['fl', 'files'],
      ['inv', 'invoices'],
      ['contract', 'contracts'],
      ['art', 'helpdesk'],
    ]);

    const shortLinkKeys = Array.from(shortLinks.keys());

    const matchedShortlinkCodes = shortLinkKeys.filter((item) =>
      baseUrl.includes(item),
    );

    const shortLinkLinkCode =
      matchedShortlinkCodes && matchedShortlinkCodes.length > 0
        ? matchedShortlinkCodes.at(0) || ''
        : '';
    return shortLinks.get(shortLinkLinkCode);
  }

  static getExtensionFromFileUrl(url: string) {
    return url.split(/#|\?/)[0]?.split('.').pop()?.trim();
  }

  static FormatUrlQueryToString(query: Record<string, string>) {
    return Object.entries(query)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
      )
      .join('&');
  }

  static startsWithProtocol(url: string) {
    return url.startsWith('http://') || url.startsWith('https://');
  }

  static GetFullUrl(url: string, options?: UrlOptions) {
    const { removeTrailingSlash = false } = options || {};

    let convertedUrl = url;
    if (removeTrailingSlash) {
      convertedUrl = url.endsWith('/') ? url.slice(0, -1) : url;
    }

    return !this.startsWithProtocol(convertedUrl) ? `https://${url}` : url;
  }

  static isLocalhost(): boolean {
    return (
      typeof window !== 'undefined' &&
      Boolean(
        window.location.hostname === 'localhost' ||
          // [::1] is the IPv6 localhost address.
          window.location.hostname === '[::1]' ||
          // 127.0.0.1/8 is considered localhost for IPv4.
          window.location.hostname.match(
            /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
          ),
      )
    );
  }

  /** Whether this page is served from a staging, preview or local stack. */
  static isStagingHost(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const { hostname } = window.location;
    return (
      this.isLocalhost() ||
      isNonDeployedHost(hostname) ||
      [
        ...allPrimaryDomainHostnames.staging,
        ...allClientDomainHostnames.staging,
      ].some((domain) => hostname === domain || hostname.endsWith(`.${domain}`))
    );
  }

  // Each stack links to its own marketing site, so a staging click lands on the
  // staging page. The bare domain, since staging has no www host.
  static getMarketingSiteUrl(): string {
    return `https://${
      this.isStagingHost()
        ? primaryDomainHostname.staging
        : primaryDomainHostname.prod
    }`;
  }

  // Staging, preview and local stacks all point at the staging MCP server.
  static getMcpServerUrl(): string {
    return `https://mcp.${
      this.isStagingHost()
        ? primaryDomainHostname.staging
        : primaryDomainHostname.prod
    }`;
  }

  static isAssemblyDomain(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return (
      // .assembly.com
      window.location.hostname.endsWith(
        `.${assemblyPrimaryDomainHostname.prod}`,
      ) ||
      // .assembly-staging.com
      window.location.hostname.endsWith(
        `.${assemblyPrimaryDomainHostname.staging}`,
      ) ||
      (this.isLocalhost() && window.location.search.includes('assembly=true'))
    );
  }
}

/**
 * Checks if the url is whitelisted
 * @param url - The url to check
 * @returns True if the url is whitelisted, false otherwise
 */
export const isWhitelistedUrl = ({
  req,
  url: _url,
}: {
  req: Omit<Request, 'portalConfig'> & {
    portalConfig: PortalConfig | undefined;
  };
  url: string;
}): boolean => {
  // strip www, the protocol and path from the url
  const url = stripUrl(_url);

  // if the url is the root path or starts with a slash
  if (url === '/' || url.at(0) === '/') return true;

  // Check if url is, or is a subdomain of, any primary or client domain
  const allDomains = [
    req.portalConfig?.SignoutRedirectURL
      ? stripUrl(req.portalConfig.SignoutRedirectURL)
      : '/',
    ...allPrimaryDomainHostnames.prod,
    ...allPrimaryDomainHostnames.staging,
    ...allClientDomainHostnames.prod,
    ...allClientDomainHostnames.staging,
  ];

  for (const domain of allDomains) {
    if (url === domain || url.endsWith(`.${domain}`)) {
      return true;
    }
  }

  // if the url's hostname matches the current hostname (ignoring protocol and path)
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname === req.hostname || urlObj.origin === req.hostname) {
      return true;
    }
  } catch {
    // If URL constructor fails (e.g., url is not absolute), skip this check
  }

  return false;
};

/**
 * Strips the www, the protocol and path from the url (unless it's a relative path)
 * @param url - The url to strip
 * @returns The stripped url (ex: https://www.example.com/path -> example.com)
 */
export const stripUrl = (url: string): string => {
  if (url.at(0) === '/') return url;

  const strippedUrl = url
    // remove the protocol
    .replace(/^https?:\/\//, '')
    // remove the www
    .replace(/^www\./, '')
    // remove the path
    .replace(/\/.*/, '');

  return strippedUrl.length > 1
    ? strippedUrl.replace(/\/$/, '') // remove the trailing slash
    : '/';
};

/**
 *
 * @param message receives a string message and parses it to HTML
 * @returns HTML string
 */
export const parseMessageToHTML = (
  _message: string | AddressCustomFieldValues,
): string => {
  if (!_message) {
    return '';
  }

  const message: string =
    typeof _message === 'object' ? _message.fullAddress : _message;

  // Converts image markdown to HTML img tags
  const imageMarkdownRegex = /!\[([^\]]*)\]\((https?:\/\/[^\s]+)\)/gi;
  const parsedWithImages = message.replace(
    imageMarkdownRegex,
    (_, alt, url) => {
      return `<img src="${url}" alt="${alt}" />`;
    },
  );

  // Converts markdown links to HTML anchor tags
  const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s]+)\)/gi;
  const parsedWithMarkdownLinks = parsedWithImages.replace(
    markdownLinkRegex,
    (_, text, url) => {
      return `<a href="${url}" target="_blank">${text}</a>`;
    },
  );

  // Split the text into segments based on HTML tags
  const segments = parsedWithMarkdownLinks.split(/(<[^>]+>)/);

  // Process each segment, only converting URLs in non-HTML segments
  const processedSegments = segments.map((segment) => {
    // Skip if segment is an HTML tag
    if (segment.startsWith('<') && segment.endsWith('>')) {
      return segment;
    }

    // Convert plain links to HTML anchor tags
    const linkRegex =
      /(https?:\/\/[\w.-]+(?:\/[\w\-.~:/?#[\]@!$&'()*+,;=%]*)?|www\.[\w.-]+\.[a-zA-Z]{2,}(?:\/[\w\-.~:/?#[\]@!$&'()*+,;=%]*)?|\b[\w.-]+\.[a-zA-Z]{2,}(?:\/[\w\-.~:/?#[\]@!$&'()*+,;=%]*)?)(?=\b|\s|$)/gi;
    return segment.replace(linkRegex, (match) => {
      // Remove trailing punctuation from match
      const cleanedMatch = match.replace(/[.,)"']+$/, '');
      const link =
        cleanedMatch.startsWith('http') || cleanedMatch.startsWith('www')
          ? cleanedMatch
          : `http://${cleanedMatch}`;
      // If we removed punctuation, add it back after the link
      const trailing = match.slice(cleanedMatch.length);
      return `<a href="${link}" target="_blank">${cleanedMatch}</a>${trailing}`;
    });
  });

  // Join segments and convert newlines
  const parsedWithLinks = processedSegments.join('');
  const parsedWithNewLines = parsedWithLinks.replace(/\n/g, '<br>');

  return parsedWithNewLines;
};

export const replaceQueryParam = (queryName: string, queryValue: string) => {
  const queryParams = new URLSearchParams(history.location.search);
  queryParams.set(queryName, queryValue);
  history.replace({
    search: queryParams.toString(),
  });
};
